import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { listGearSets } from "../../../api";
import { trackProductEvent } from "../../../analytics/trackEvent";
import { getActiveAwareAuthToken } from "../../../auth/bootstrapTokens";
import {
  buildFishSpeciesSuggestions,
  getTypeOfFishingOptionsForWay,
  isLureFishingType,
} from "../../../utils/catchMetadataOptions";
import { getFishSpeciesValidationErrorKey } from "../../../utils/fishSpeciesValidation";
import { normalizeCoords } from "./CatchFormValidation";
import {
  getReelSuggestions,
  getRodSuggestions,
} from "./catchFormGearHelpers";
import useCatchConsentFlow from "./useCatchConsentFlow";
import useCatchLocationState from "./useCatchLocationState";
import useCatchMediaState from "./useCatchMediaState";
import useCatchSubmitFlow from "./useCatchSubmitFlow";

const getDraftString = (value) =>
  typeof value === "string" ? value : value == null ? "" : String(value);

const getDraftBoolean = (value, fallback = false) =>
  typeof value === "boolean" ? value : fallback;

const getDraftNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeSavedGearSet = (rawGearSet) => ({
  id: getDraftString(rawGearSet?.id),
  name: getDraftString(rawGearSet?.name),
  rod: getDraftString(rawGearSet?.rod),
  reel: getDraftString(rawGearSet?.reel),
  line: getDraftString(rawGearSet?.line),
  leader: getDraftString(rawGearSet?.leader),
  lure: getDraftString(rawGearSet?.lure),
  hook: getDraftString(rawGearSet?.hook),
});

export default function useCatchFormState({
  pickedCoordinates,
  tripId,
  onClose,
  onAddPost,
  onSubmittingChange,
  onDirtyChange,
  isSimpleModeActive = false,
  user,
  onUserUpdated,
  rodDatabase,
  reelDatabase,
  braidDatabase,
  leaderDatabase,
  lureDatabase,
  hookBrandDatabase,
  baitDatabase,
  liveBaitDatabase,
  deadBaitDatabase,
  fishingMethods,
  fishList,
  lureSizeOptions,
  combinedHookOptions,
  draftCatchMedia,
  setDraftCatchMedia,
  draftCatchFormState,
  setDraftCatchFormState,
  draftExifDate,
  setDraftExifDate,
  draftExifTime,
  setDraftExifTime,
}) {
  const { t } = useTranslation();

  const playPostSuccessSound = useCallback(() => {
    const audio = new Audio(process.env.PUBLIC_URL + "/sound-efects/reel.mp3");
    audio.volume = 0.8;
    audio.play().catch(() => {});
  }, []);

  const [caption, setCaption] = useState("");
  const [rating, setRating] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [fishInput, setFishInput] = useState("");
  const [fishSuggestions, setFishSuggestions] = useState([]);
  const [size, setSize] = useState("");
  const [weight, setWeight] = useState("");
  const [sizeUnit, setSizeUnit] = useState("cm");
  const [sizeFeet, setSizeFeet] = useState("");
  const [sizeInches, setSizeInches] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [weightLb, setWeightLb] = useState("");
  const [waterType, setWaterType] = useState("all");
  const [wayOfFishing, setWayOfFishing] = useState("");
  const [typeOfFishing, setTypeOfFishing] = useState("");
  const [typeOfFishingOptions, setTypeOfFishingOptions] = useState([]);
  const [style, setStyle] = useState("");
  const [styleOptions, setStyleOptions] = useState([]);
  const [customStyle] = useState("");
  const [technique, setTechnique] = useState("");
  const [techniqueOptions, setTechniqueOptions] = useState([]);
  const [rod, setRod] = useState("");
  const [reel, setReel] = useState("");
  const [braid, setBraid] = useState("");
  const [leader, setLeader] = useState("");
  const [lure, setLure] = useState("");
  const [lureSize, setLureSize] = useState("");
  const [lureColor, setLureColor] = useState("");
  const [hookBrand, setHookBrand] = useState("");
  const [hookSpec, setHookSpec] = useState("");
  const [knotBraidToLeader, setKnotBraidToLeader] = useState("");
  const [knotLeaderToLure, setKnotLeaderToLure] = useState("");
  const [catchAndRelease, setCatchAndRelease] = useState("");
  const [savedGearSets, setSavedGearSets] = useState([]);
  const [savedGearSetId, setSavedGearSetId] = useState("");
  const [isLoadingSavedGearSets, setIsLoadingSavedGearSets] = useState(false);
  const [savedGearSetLoadError, setSavedGearSetLoadError] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const selectionIdRef = useRef(0);
  const mediaApiRef = useRef(null);
  const mapPickerDraftRef = useRef(null);
  const restoredDraftRef = useRef(null);
  const getMapPickerDetail = useCallback(
    () => ({ draftFormState: mapPickerDraftRef.current }),
    []
  );

  const getCurrentMediaState = useCallback(
    () => mediaApiRef.current?.mediaState || null,
    []
  );

  const locationState = useCatchLocationState({
    pickedCoordinates,
    t,
    getCurrentMediaState,
    setDraftCatchMedia,
    draftExifDate,
    setDraftExifDate,
    draftExifTime,
    setDraftExifTime,
    mapPickerDetail: getMapPickerDetail,
  });
  const {
    setCoordinates,
    setIsLocationPublic,
    setLocation,
    setExifDate,
    setExifTime,
    setLocationMode,
    setUseManualDateTime,
  } = locationState;

  const consentFlow = useCatchConsentFlow({
    onUserUpdated,
    getCurrentSelectionId: useCallback(() => selectionIdRef.current, []),
  });

  const resetFormState = useCallback(() => {
    locationState.clearLocationState();
    mediaApiRef.current?.clearMediaState?.();
    setCaption("");
    if (typeof setDraftExifDate === "function") {
      setDraftExifDate(null);
    }
    if (typeof setDraftExifTime === "function") {
      setDraftExifTime(null);
    }
    if (typeof setDraftCatchFormState === "function") {
      setDraftCatchFormState(null);
    }
  }, [
    locationState,
    setDraftCatchFormState,
    setDraftExifDate,
    setDraftExifTime,
  ]);

  const getMediaApi = useCallback(() => mediaApiRef.current, []);

  const getFormSnapshot = useCallback(
    () => {
      const snapshot = {
        fishInput,
        waterType,
        wayOfFishing,
        typeOfFishing,
        style,
        customStyle,
        technique,
        rod,
        reel,
        braid,
        leader,
        lure,
        lureSize,
        lureColor,
        hookBrand,
        hookSpec,
        knotBraidToLeader,
        knotLeaderToLure,
        catchAndRelease,
        coordinates: locationState.coordinates,
        isLocationPublic: locationState.isLocationPublic,
        location: locationState.location,
        exifDate: locationState.exifDate,
        exifTime: locationState.exifTime,
        rating,
        feedback,
        caption,
        sizeUnit,
        size,
        sizeFeet,
        sizeInches,
        weightUnit,
        weight,
        weightLb,
        tripId,
        requiresLocationSharingConsent: Boolean(
          user?.requiresLocationSharingConsent
        ),
      };

      if (!isSimpleModeActive) {
        return snapshot;
      }

      return {
        ...snapshot,
        waterType: "all",
        wayOfFishing: "",
        typeOfFishing: "",
        style: "",
        customStyle: "",
        technique: "",
        rod: "",
        reel: "",
        braid: "",
        leader: "",
        lure: "",
        lureSize: "",
        lureColor: "",
        hookBrand: "",
        hookSpec: "",
        knotBraidToLeader: "",
        knotLeaderToLure: "",
        catchAndRelease: "",
        feedback: "",
      };
    },
    [
      braid,
      caption,
      catchAndRelease,
      customStyle,
      feedback,
      fishInput,
      hookBrand,
      hookSpec,
      isSimpleModeActive,
      knotBraidToLeader,
      knotLeaderToLure,
      leader,
      locationState.coordinates,
      locationState.exifDate,
      locationState.exifTime,
      locationState.isLocationPublic,
      locationState.location,
      lure,
      lureColor,
      lureSize,
      rating,
      reel,
      rod,
      size,
      sizeFeet,
      sizeInches,
      sizeUnit,
      style,
      technique,
      tripId,
      typeOfFishing,
      user?.requiresLocationSharingConsent,
      waterType,
      wayOfFishing,
      weight,
      weightLb,
      weightUnit,
    ]
  );

  const getMapPickerDraftFormState = useCallback(
    () => ({
      caption,
      rating,
      showFeedback,
      feedback,
      fishInput,
      size,
      weight,
      sizeUnit,
      sizeFeet,
      sizeInches,
      weightUnit,
      weightLb,
      waterType,
      wayOfFishing,
      typeOfFishing,
      style,
      technique,
      rod,
      reel,
      braid,
      leader,
      lure,
      lureSize,
      lureColor,
      hookBrand,
      hookSpec,
      knotBraidToLeader,
      knotLeaderToLure,
      catchAndRelease,
      location: {
        coordinates: locationState.coordinates,
        isLocationPublic: locationState.isLocationPublic,
        location: locationState.location,
        locationMode: locationState.locationMode,
        exifDate: locationState.exifDate,
        exifTime: locationState.exifTime,
        useManualDateTime: locationState.useManualDateTime,
      },
    }),
    [
      braid,
      caption,
      catchAndRelease,
      feedback,
      fishInput,
      hookBrand,
      hookSpec,
      knotBraidToLeader,
      knotLeaderToLure,
      leader,
      locationState.coordinates,
      locationState.exifDate,
      locationState.exifTime,
      locationState.isLocationPublic,
      locationState.location,
      locationState.locationMode,
      locationState.useManualDateTime,
      lure,
      lureColor,
      lureSize,
      rating,
      reel,
      rod,
      showFeedback,
      size,
      sizeFeet,
      sizeInches,
      sizeUnit,
      style,
      technique,
      typeOfFishing,
      waterType,
      wayOfFishing,
      weight,
      weightLb,
      weightUnit,
    ]
  );
  mapPickerDraftRef.current = getMapPickerDraftFormState();

  useLayoutEffect(() => {
    if (!draftCatchFormState || typeof draftCatchFormState !== "object") {
      return;
    }

    if (restoredDraftRef.current === draftCatchFormState) {
      return;
    }
    restoredDraftRef.current = draftCatchFormState;

    const draftLocation =
      draftCatchFormState.location &&
      typeof draftCatchFormState.location === "object"
        ? draftCatchFormState.location
        : {};
    const pickedMapCoordinates = normalizeCoords(pickedCoordinates || "");
    const restoredCoordinates =
      pickedMapCoordinates || getDraftString(draftLocation.coordinates);

    setCaption(getDraftString(draftCatchFormState.caption));
    setRating(getDraftNumber(draftCatchFormState.rating, 0));
    setShowFeedback(
      getDraftBoolean(
        draftCatchFormState.showFeedback,
        Boolean(draftCatchFormState.feedback)
      )
    );
    setFeedback(getDraftString(draftCatchFormState.feedback));
    setFishInput(getDraftString(draftCatchFormState.fishInput));
    setFishSuggestions([]);
    setSize(getDraftString(draftCatchFormState.size));
    setWeight(getDraftString(draftCatchFormState.weight));
    setSizeUnit(getDraftString(draftCatchFormState.sizeUnit) || "cm");
    setSizeFeet(getDraftString(draftCatchFormState.sizeFeet));
    setSizeInches(getDraftString(draftCatchFormState.sizeInches));
    setWeightUnit(getDraftString(draftCatchFormState.weightUnit) || "kg");
    setWeightLb(getDraftString(draftCatchFormState.weightLb));
    setWaterType(getDraftString(draftCatchFormState.waterType) || "all");
    setWayOfFishing(getDraftString(draftCatchFormState.wayOfFishing));
    setTypeOfFishing(getDraftString(draftCatchFormState.typeOfFishing));
    setStyle(getDraftString(draftCatchFormState.style));
    setTechnique(getDraftString(draftCatchFormState.technique));
    setRod(getDraftString(draftCatchFormState.rod));
    setReel(getDraftString(draftCatchFormState.reel));
    setBraid(getDraftString(draftCatchFormState.braid));
    setLeader(getDraftString(draftCatchFormState.leader));
    setLure(getDraftString(draftCatchFormState.lure));
    setLureSize(getDraftString(draftCatchFormState.lureSize));
    setLureColor(getDraftString(draftCatchFormState.lureColor));
    setHookBrand(getDraftString(draftCatchFormState.hookBrand));
    setHookSpec(getDraftString(draftCatchFormState.hookSpec));
    setKnotBraidToLeader(
      getDraftString(draftCatchFormState.knotBraidToLeader)
    );
    setKnotLeaderToLure(
      getDraftString(draftCatchFormState.knotLeaderToLure)
    );
    setCatchAndRelease(getDraftString(draftCatchFormState.catchAndRelease));
    setCoordinates(restoredCoordinates);
    setLocation(pickedMapCoordinates ? "" : getDraftString(draftLocation.location));
    setIsLocationPublic(getDraftBoolean(draftLocation.isLocationPublic, true));
    setLocationMode(
      pickedMapCoordinates
        ? "map"
        : getDraftString(draftLocation.locationMode) ||
            (restoredCoordinates ? "manual" : "none")
    );
    setExifDate(getDraftString(draftLocation.exifDate));
    setExifTime(getDraftString(draftLocation.exifTime));
    setUseManualDateTime(getDraftBoolean(draftLocation.useManualDateTime));
  }, [
    draftCatchFormState,
    pickedCoordinates,
    setCoordinates,
    setExifDate,
    setExifTime,
    setIsLocationPublic,
    setLocation,
    setLocationMode,
    setUseManualDateTime,
  ]);

  const submitFlow = useCatchSubmitFlow({
    t,
    onClose,
    onAddPost,
    onSubmittingChange,
    selectionIdRef,
    getMediaApi,
    getFormSnapshot,
    requestLocationConsent: consentFlow.requestLocationConsent,
    resetFormState,
    playPostSuccessSound,
  });

  const mediaStateApi = useCatchMediaState({
    draftCatchMedia,
    setDraftCatchMedia,
    selectionIdRef,
    submitTransactionRef: submitFlow.submitTransactionRef,
    setSubmitPhase: submitFlow.setSubmitPhase,
    setSubmitStatus: submitFlow.setSubmitStatus,
    t,
    user,
    requestExifConsent: consentFlow.requestExifConsent,
    resetSelectionDerivedConsentState:
      consentFlow.resetSelectionDerivedConsentState,
    resetSelectionDerivedLocationState:
      locationState.resetSelectionDerivedLocationState,
    applyDetectedExifDate: locationState.applyDetectedExifDate,
    applyDetectedExifTime: locationState.applyDetectedExifTime,
    getManualDateTimeEditVersion:
      locationState.getManualDateTimeEditVersion,
    setExifGps: locationState.setExifGps,
    setCoordsFromExifIfEmpty: locationState.setCoordsFromExifIfEmpty,
  });

  mediaApiRef.current = mediaStateApi;

  const handleAcceptExifConsent = useCallback(async () => {
    const applySelectedMedia = mediaApiRef.current?.applySelectedMedia;
    if (typeof applySelectedMedia !== "function") {
      return;
    }
    await consentFlow.handleAcceptExifConsent(applySelectedMedia);
  }, [consentFlow]);

  const handleAcceptLocationConsent = useCallback(async () => {
    await consentFlow.handleAcceptLocationConsent(submitFlow.handlePostSubmit);
  }, [consentFlow, submitFlow.handlePostSubmit]);

  const isFormDirty = useMemo(() => {
    const hasText = (value) =>
      typeof value === "string" ? value.trim().length > 0 : Boolean(value);
    const mediaState = mediaStateApi.mediaState || {};
    const hasMedia = Boolean(
      mediaState.selectedFile ||
        mediaState.uploadFile ||
        mediaState.originalFile ||
        mediaState.snapshotFile ||
        mediaState.previewUrl ||
        mediaStateApi.selectedFile ||
        mediaStateApi.imagePreviewUrl
    );

    return Boolean(
      hasMedia ||
        hasText(caption) ||
        hasText(locationState.coordinates) ||
        hasText(locationState.location) ||
        hasText(fishInput) ||
        hasText(size) ||
        hasText(sizeFeet) ||
        hasText(sizeInches) ||
        hasText(weight) ||
        hasText(weightLb) ||
        rating > 0 ||
        hasText(feedback) ||
        waterType !== "all" ||
        hasText(wayOfFishing) ||
        hasText(typeOfFishing) ||
        hasText(style) ||
        hasText(customStyle) ||
        hasText(technique) ||
        hasText(savedGearSetId) ||
        hasText(rod) ||
        hasText(reel) ||
        hasText(braid) ||
        hasText(leader) ||
        hasText(lure) ||
        hasText(lureSize) ||
        hasText(lureColor) ||
        hasText(hookBrand) ||
        hasText(hookSpec) ||
        hasText(knotBraidToLeader) ||
        hasText(knotLeaderToLure) ||
        hasText(catchAndRelease) ||
        hasText(locationState.exifDate) ||
        hasText(locationState.exifTime)
    );
  }, [
    braid,
    caption,
    catchAndRelease,
    customStyle,
    feedback,
    fishInput,
    hookBrand,
    hookSpec,
    knotBraidToLeader,
    knotLeaderToLure,
    leader,
    locationState.coordinates,
    locationState.exifDate,
    locationState.exifTime,
    locationState.location,
    lure,
    lureColor,
    lureSize,
    mediaStateApi.imagePreviewUrl,
    mediaStateApi.mediaState,
    mediaStateApi.selectedFile,
    rating,
    reel,
    rod,
    savedGearSetId,
    size,
    sizeFeet,
    sizeInches,
    style,
    technique,
    typeOfFishing,
    waterType,
    wayOfFishing,
    weight,
    weightLb,
  ]);

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange]
  );

  useEffect(() => {
    if (!user?.id) {
      setSavedGearSets([]);
      setSavedGearSetId("");
      setSavedGearSetLoadError("");
      setIsLoadingSavedGearSets(false);
      return undefined;
    }

    const token =
      typeof window !== "undefined" ? getActiveAwareAuthToken(window) : "";

    const controller = new AbortController();
    setIsLoadingSavedGearSets(true);
    setSavedGearSetLoadError("");

    listGearSets(token || "", { signal: controller.signal })
      .then((gearSets) => {
        setSavedGearSets(
          Array.isArray(gearSets) ? gearSets.map(normalizeSavedGearSet) : []
        );
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setSavedGearSets([]);
        setSavedGearSetId("");
        setSavedGearSetLoadError("catch_saved_gear_load_error");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingSavedGearSets(false);
        }
      });

    return () => controller.abort();
  }, [user?.id]);

  const applySavedGearSet = useCallback((gearSet) => {
    const savedRod = getDraftString(gearSet?.rod).trim();
    const savedReel = getDraftString(gearSet?.reel).trim();
    const savedLine = getDraftString(gearSet?.line).trim();
    const savedLeader = getDraftString(gearSet?.leader).trim();
    const savedLure = getDraftString(gearSet?.lure).trim();
    const savedHook = getDraftString(gearSet?.hook).trim();

    if (savedRod) setRod(savedRod);
    if (savedReel) setReel(savedReel);
    if (savedLine) setBraid(savedLine);
    if (savedLeader) setLeader(savedLeader);
    if (savedLure) setLure(savedLure);
    if (savedHook) setHookBrand(savedHook);
    setActiveDropdown(null);
  }, []);

  const handleSavedGearSetChange = useCallback(
    (event) => {
      const nextGearSetId = event.target.value;
      if (!nextGearSetId) {
        setSavedGearSetId("");
        return;
      }

      const selectedGearSet = savedGearSets.find(
        (gearSet) => gearSet.id === nextGearSetId
      );
      if (!selectedGearSet) return;

      const hasCurrentGearFields = [rod, reel, braid, leader, lure, hookBrand].some(
        (value) => getDraftString(value).trim()
      );
      const shouldApply =
        !hasCurrentGearFields ||
        typeof window === "undefined" ||
        window.confirm(t("catch_saved_gear_apply_confirm"));

      if (!shouldApply) return;

      applySavedGearSet(selectedGearSet);
      setSavedGearSetId(nextGearSetId);
      trackProductEvent("saved_gear_applied_to_catch", {
        featureArea: "create_post",
        metadata: {
          source: "catch_form_saved_gear_select",
          replaced_existing_gear: Boolean(hasCurrentGearFields),
          has_rod: Boolean(getDraftString(selectedGearSet.rod).trim()),
          has_reel: Boolean(getDraftString(selectedGearSet.reel).trim()),
          has_line: Boolean(getDraftString(selectedGearSet.line).trim()),
          has_leader: Boolean(getDraftString(selectedGearSet.leader).trim()),
          has_lure: Boolean(getDraftString(selectedGearSet.lure).trim()),
          has_hook: Boolean(getDraftString(selectedGearSet.hook).trim()),
        },
      });
    },
    [
      applySavedGearSet,
      braid,
      hookBrand,
      leader,
      lure,
      reel,
      rod,
      savedGearSets,
      t,
    ]
  );

  useEffect(() => {
    if (
      !wayOfFishing ||
      !typeOfFishing ||
      !fishingMethods ||
      !fishingMethods[wayOfFishing]
    ) {
      setStyleOptions([]);
      setStyle("");
      return;
    }

    const methods = fishingMethods[wayOfFishing];
    const matchedKey = Object.keys(methods).find(
      (key) => key.toLowerCase().trim() === typeOfFishing.toLowerCase().trim()
    );

    if (matchedKey && Array.isArray(methods[matchedKey])) {
      const nextStyleOptions = methods[matchedKey];
      setStyleOptions(nextStyleOptions);
      setStyle((currentStyle) =>
        currentStyle && nextStyleOptions.includes(currentStyle)
          ? currentStyle
          : ""
      );
    } else {
      setStyleOptions([]);
      setStyle("");
    }
  }, [fishingMethods, typeOfFishing, wayOfFishing]);

  useEffect(() => {
    if (
      !wayOfFishing ||
      !typeOfFishing ||
      !style ||
      !fishingMethods?.techniques?.[wayOfFishing]?.[typeOfFishing]?.[style]
    ) {
      setTechniqueOptions([]);
      setTechnique("");
      return;
    }

    const list = fishingMethods.techniques[wayOfFishing][typeOfFishing][style];
    if (Array.isArray(list)) {
      setTechniqueOptions(list);
      setTechnique((currentTechnique) =>
        currentTechnique && list.includes(currentTechnique)
          ? currentTechnique
          : ""
      );
    } else {
      setTechniqueOptions([]);
      setTechnique("");
    }
  }, [fishingMethods, style, typeOfFishing, wayOfFishing]);

  useEffect(() => {
    setTypeOfFishingOptions(
      getTypeOfFishingOptionsForWay(fishingMethods, wayOfFishing)
    );
  }, [fishingMethods, wayOfFishing]);

  const handleWayChange = useCallback((event) => {
    const selectedWay = event.target.value;
    setWayOfFishing(selectedWay);
    setTypeOfFishing("");
    setStyle("");
    setTechnique("");
    setStyleOptions([]);
    setTechniqueOptions([]);
  }, []);

  const handleStarClick = useCallback((value) => {
    setRating(value);
    setShowFeedback(true);
  }, []);

  const handleFishInputChange = useCallback(
    (event) => {
      const value = event.target.value;
      setFishInput(value);

      if (value.length > 1 && fishList?.length) {
        const filtered = buildFishSpeciesSuggestions(fishList, value);
        setFishSuggestions(filtered);
      } else {
        setFishSuggestions([]);
      }
    },
    [fishList]
  );

  const handleSelectFishSuggestion = useCallback((fish) => {
    setFishInput(fish.commonName);
    setFishSuggestions([]);
  }, []);

  const fishSpeciesErrorKey = getFishSpeciesValidationErrorKey(fishInput);
  const rodSuggestions =
    activeDropdown === "Rod" && rod ? getRodSuggestions(rodDatabase, rod) : [];
  const reelSuggestions =
    activeDropdown === "Reel" && reel ? getReelSuggestions(reelDatabase, reel) : [];

  return {
    t,
    isSimpleModeActive,
    rodDatabase,
    reelDatabase,
    braidDatabase,
    leaderDatabase,
    lureDatabase,
    hookBrandDatabase,
    baitDatabase,
    liveBaitDatabase,
    deadBaitDatabase,
    lureSizeOptions,
    combinedHookOptions,
    typeOfFishing,
    caption,
    setCaption,
    mediaState: mediaStateApi.mediaState,
    selectedFile: mediaStateApi.selectedFile,
    selectedMediaType: mediaStateApi.selectedMediaType,
    imagePreviewUrl: mediaStateApi.imagePreviewUrl,
    previewError: mediaStateApi.previewError,
    exifDate: locationState.exifDate,
    exifTime: locationState.exifTime,
    useManualDateTime: locationState.useManualDateTime,
    setUseManualDateTime: locationState.setUseManualDateTime,
    locationMode: locationState.locationMode,
    locationHint: locationState.locationHint,
    coordinates: locationState.coordinates,
    isLocationPublic: locationState.isLocationPublic,
    fishInput,
    fishSpeciesErrorKey,
    hasFishSpeciesError: Boolean(fishSpeciesErrorKey),
    fishSuggestions,
    size,
    setSize,
    weight,
    setWeight,
    sizeUnit,
    setSizeUnit,
    sizeFeet,
    setSizeFeet,
    sizeInches,
    setSizeInches,
    weightUnit,
    setWeightUnit,
    weightLb,
    setWeightLb,
    waterType,
    setWaterType,
    wayOfFishing,
    typeOfFishingOptions,
    setTypeOfFishing,
    style,
    setStyle,
    styleOptions,
    technique,
    setTechnique,
    techniqueOptions,
    rod,
    setRod,
    reel,
    setReel,
    activeDropdown,
    setActiveDropdown,
    rodSuggestions,
    reelSuggestions,
    savedGearSets,
    savedGearSetId,
    isLoadingSavedGearSets,
    savedGearSetLoadError,
    handleSavedGearSetChange,
    braid,
    setBraid,
    leader,
    setLeader,
    lure,
    setLure,
    lureSize,
    setLureSize,
    lureColor,
    setLureColor,
    hookBrand,
    setHookBrand,
    hookSpec,
    setHookSpec,
    knotBraidToLeader,
    setKnotBraidToLeader,
    knotLeaderToLure,
    setKnotLeaderToLure,
    catchAndRelease,
    setCatchAndRelease,
    rating,
    feedback,
    setFeedback,
    showFeedback,
    showExifConsent: consentFlow.showExifConsent,
    isSavingExifConsent: consentFlow.isSavingExifConsent,
    exifConsentError: consentFlow.exifConsentError,
    showLocationConsent: consentFlow.showLocationConsent,
    isSavingLocationConsent: consentFlow.isSavingLocationConsent,
    locationConsentError: consentFlow.locationConsentError,
    submitStatusMessage: submitFlow.submitStatusMessage,
    submitStatusTone: submitFlow.submitStatusTone,
    submitPhase: submitFlow.submitPhase,
    mediaError: mediaStateApi.mediaError,
    isSubmitting: submitFlow.isSubmitting,
    handleUseGps: locationState.handleUseGps,
    handlePickFromMap: locationState.handlePickFromMap,
    handleStartManualCoordinates: locationState.handleStartManualCoordinates,
    handleCancelManualCoordinates: locationState.handleCancelManualCoordinates,
    handleImageUpload: mediaStateApi.handleImageUpload,
    handleImagePreviewError: mediaStateApi.handleImagePreviewError,
    handleManualDateChange: locationState.handleManualDateChange,
    handleManualTimeChange: locationState.handleManualTimeChange,
    handleCoordinatesChange: locationState.handleCoordinatesChange,
    handleCoordinatesBlur: locationState.handleCoordinatesBlur,
    handleSetLocationPublic: locationState.handleSetLocationPublic,
    handleSetLocationPrivate: locationState.handleSetLocationPrivate,
    handleClearCoordinates: locationState.handleClearCoordinates,
    handleFishInputChange,
    handleSelectFishSuggestion,
    handleWayChange,
    handleStarClick,
    handleCancel: submitFlow.handleCancel,
    handlePostSubmit: submitFlow.handlePostSubmit,
    handleAcceptExifConsent,
    handleCancelExifConsent: consentFlow.handleCancelExifConsent,
    handleAcceptLocationConsent,
    handleCancelLocationConsent: consentFlow.handleCancelLocationConsent,
    normalizeCoords,
    submitReady: mediaStateApi.submitReady,
    isMissingMedia: !mediaStateApi.submitReady,
    isLureFishingType,
  };
}
